/**
 * CompanyDetailsForm — единая форма реквизитов компании (1.32.0)
 *
 * Используется в двух местах:
 *   • CompanySetupModal — при первом входе админа (allowSkip=true)
 *   • SystemSettings    — в режиме редактирования (allowSkip=false)
 *
 * Источник правды — таблица public.company_details. Один компонент
 * на оба случая, чтобы поля и валидация не расходились.
 *
 * DaData используется в поле "Фактический адрес": подсказки при вводе,
 * автозаполнение координат и часового пояса при выборе подсказки.
 *
 * Обязательное поле — только timezone (без него не работают
 * cron-уведомления утренней сводки и тихие часы).
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { C, F } from '../design-system/tokens';
import { Button } from '../design-system/atoms/Button';
import { fetchCompanyDetails, upsertCompanyDetails } from '../domains/companies/api';
import type {
  CompanyDetailsInput,
  LegalForm,
  TaxRegime,
} from '../domains/companies/types';
import { suggestAddress, type AddressSuggestion } from './utils/dadata';
import { uploadCompanyBranding, deleteCompanyBranding, type BrandingKind } from './utils/companyBranding';

// ─── Константы ─────────────────────────────────────────────────────────────

const LEGAL_FORM_OPTIONS: Array<{ value: LegalForm; label: string }> = [
  { value: 'individual_entrepreneur', label: 'ИП' },
  { value: 'llc', label: 'ООО' },
  { value: 'self_employed', label: 'Самозанятый' },
  { value: 'other', label: 'Другая' },
];

const TAX_REGIME_OPTIONS: Array<{ value: TaxRegime; label: string }> = [
  { value: 'usn_income', label: 'УСН Доходы' },
  { value: 'usn_income_expense', label: 'УСН Доходы минус Расходы' },
  { value: 'osn', label: 'ОСНО' },
  { value: 'patent', label: 'Патент' },
  { value: 'npd', label: 'НПД (Самозанятый)' },
  { value: 'other', label: 'Другой' },
];

/**
 * Российские IANA-таймзоны (fallback-селект, если DaData не вернула
 * таймзону или админ заполняет данные без DaData).
 */
const TIMEZONE_OPTIONS = [
  { value: 'Europe/Kaliningrad', label: 'Калининград (UTC+2)' },
  { value: 'Europe/Moscow', label: 'Москва, СПб (UTC+3)' },
  { value: 'Europe/Samara', label: 'Самара, Ижевск (UTC+4)' },
  { value: 'Asia/Yekaterinburg', label: 'Екатеринбург, Челябинск (UTC+5)' },
  { value: 'Asia/Omsk', label: 'Омск (UTC+6)' },
  { value: 'Asia/Krasnoyarsk', label: 'Красноярск, Новосибирск (UTC+7)' },
  { value: 'Asia/Irkutsk', label: 'Иркутск (UTC+8)' },
  { value: 'Asia/Yakutsk', label: 'Якутск, Чита (UTC+9)' },
  { value: 'Asia/Vladivostok', label: 'Владивосток (UTC+10)' },
  { value: 'Asia/Magadan', label: 'Магадан, Сахалин (UTC+11)' },
  { value: 'Asia/Kamchatka', label: 'Камчатка, Анадырь (UTC+12)' },
];

// ─── Стили ─────────────────────────────────────────────────────────────────

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontFamily: F.head,
  fontWeight: 600,
  letterSpacing: 1,
  color: C.sub,
  textTransform: 'uppercase' as const,
  marginBottom: 6,
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  background: C.bg,
  border: `1px solid ${C.b}`,
  borderRadius: 10,
  color: C.txt,
  fontFamily: F.body,
  fontSize: 15,
  outline: 'none',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: 13,
  fontFamily: F.head,
  fontWeight: 700,
  letterSpacing: 1.5,
  color: C.acc,
  textTransform: 'uppercase' as const,
  marginTop: 22,
  marginBottom: 14,
  paddingBottom: 8,
  borderBottom: `1px solid ${C.b}`,
};

// ─── AddressAutocomplete (внутренний) ──────────────────────────────────────

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelectSuggestion: (s: AddressSuggestion) => void;
  placeholder?: string;
}

function AddressAutocomplete({
  value,
  onChange,
  onSelectSuggestion,
  placeholder,
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Подсказки запрашиваем и раскрываем только когда пользователь сам печатает,
  // а не когда value меняется программно (загрузка сохранённого адреса или
  // выбор подсказки). Иначе дропдаун раскрывался сам при открытии формы.
  const userTypedRef = useRef(false);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    if (!userTypedRef.current) return;

    if (value.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    debounceRef.current = window.setTimeout(async () => {
      const ctl = new AbortController();
      abortRef.current = ctl;
      setLoading(true);
      try {
        const result = await suggestAddress(value, { signal: ctl.signal });
        setSuggestions(result);
        setOpen(result.length > 0);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [value]);

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        value={value}
        onChange={(e) => { userTypedRef.current = true; onChange(e.target.value); }}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder ?? 'Начните вводить адрес...'}
        style={inputStyle}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: C.cardHi,
            border: `1px solid ${C.bBright}`,
            borderRadius: 10,
            marginTop: 4,
            maxHeight: 260,
            overflowY: 'auto',
            zIndex: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}
        >
          {suggestions.map((s, i) => (
            <button
              key={`${s.value}-${i}`}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                userTypedRef.current = false;
                onChange(s.value);
                onSelectSuggestion(s);
                setOpen(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '10px 14px',
                background: 'transparent',
                border: 'none',
                borderBottom: `1px solid ${C.b}`,
                color: C.txt,
                fontFamily: F.body,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              {s.value}
            </button>
          ))}
        </div>
      )}
      {loading && (
        <div style={{ position: 'absolute', right: 14, top: 14, fontSize: 11, color: C.sub }}>
          ...
        </div>
      )}
    </div>
  );
}

// ─── Дефолтный empty-state формы ──────────────────────────────────────────

const EMPTY: CompanyDetailsInput = {
  legal_form: null,
  inn: null,
  kpp: null,
  ogrn: null,
  tax_regime: null,
  timezone: null,
  legal_address: null,
  actual_address: null,
  actual_address_lat: null,
  actual_address_lon: null,
  contact_phone: null,
  contact_email: null,
  website: null,
  logo_url: null,
  director_name: null,
  signatory_position: null,
  signatory_basis: null,
  bank_account: null,
  bank_name: null,
  bank_bic: null,
  bank_correspondent_account: null,
  sbp_phone: null,
  stamp_url: null,
  signature_url: null,
};

// ─── Загрузка брендинга (логотип / печать / подпись) ────────────────────────

interface BrandingUploadProps {
  companyId: string;
  kind: BrandingKind;
  label: string;
  hint?: string;
  value: string | null;
  onChange: (url: string | null) => void;
}

function BrandingUpload({ companyId, kind, label, hint, value, onChange }: BrandingUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      const url = await uploadCompanyBranding(companyId, kind, file);
      onChange(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Ошибка загрузки');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function handleRemove() {
    setBusy(true);
    setErr(null);
    try {
      await deleteCompanyBranding(companyId, kind);
      onChange(null);
    } catch {
      // даже если файла не было — просто очищаем ссылку
      onChange(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 6, fontWeight: 600 }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 64, height: 64, borderRadius: 10, border: `1px solid ${C.b}`,
          background: C.surf, display: 'grid', placeItems: 'center', overflow: 'hidden', flexShrink: 0,
        }}>
          {value ? (
            <img src={value} alt={label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <span style={{ fontSize: 22, opacity: 0.4 }}>🖼️</span>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            style={{ display: 'none' }}
            onChange={(e) => void handleFile(e.target.files?.[0])}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              ch={busy ? 'Загрузка...' : (value ? 'Заменить' : 'Загрузить')}
              col="acc"
              sz="sm"
              onClick={() => inputRef.current?.click()}
            />
            {value && (
              <Button ch="Удалить" col="ghost" sz="sm" onClick={() => void handleRemove()} />
            )}
          </div>
          {hint && <div style={{ fontSize: 11, color: C.sub, lineHeight: 1.4 }}>{hint}</div>}
        </div>
      </div>
      {err && <div style={{ fontSize: 11, color: C.re, marginTop: 6 }}>{err}</div>}
    </div>
  );
}

// ─── Главный компонент ────────────────────────────────────────────────────

export interface CompanyDetailsFormProps {
  companyId: string;
  /** Можно ли закрыть форму без сохранения. true в модалке первого
   *  входа (даёт кнопку "Пропустить"), false при редактировании. */
  allowSkip?: boolean;
  /** Вызывается при закрытии формы (Skip или после Save). */
  onClose: () => void;
  /** Вызывается после успешного сохранения. */
  onSaved?: () => void;
}

export function CompanyDetailsForm({
  companyId,
  allowSkip = false,
  onClose,
  onSaved,
}: CompanyDetailsFormProps) {
  const [form, setForm] = useState<CompanyDetailsInput>(EMPTY);
  const [explanationOpen, setExplanationOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Подтянуть существующую запись при открытии
  useEffect(() => {
    void (async () => {
      try {
        const existing = await fetchCompanyDetails(companyId);
        if (existing) {
          const {
            id: _id,
            company_id: _cid,
            created_at: _ca,
            updated_at: _ua,
            ...rest
          } = existing;
          setForm(rest);
        }
      } catch {
        // ignore — форма остаётся пустой
      }
    })();
  }, [companyId]);

  const update = <K extends keyof CompanyDetailsInput>(
    key: K,
    value: CompanyDetailsInput[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (error) setError(null);
  };

  // Пустая строка → null (для optional-полей)
  const nullable = (v: string): string | null => (v.trim() === '' ? null : v);

  const handleAddressSelect = (s: AddressSuggestion) => {
    setForm((prev) => ({
      ...prev,
      actual_address: s.value,
      actual_address_lat: s.lat,
      actual_address_lon: s.lon,
      // Заполняем таймзону только если ещё не выбрана
      timezone: prev.timezone ?? s.timezone,
    }));
  };

  const canSave = useMemo(() => !!form.timezone, [form.timezone]);

  const handleSave = async () => {
    if (!canSave) {
      setError('Укажите часовой пояс — он нужен для уведомлений.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await upsertCompanyDetails(companyId, form);
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* "Зачем это нужно" — свёрнутый блок */}
      <button
        type="button"
        onClick={() => setExplanationOpen(!explanationOpen)}
        style={{
          padding: '6px 12px',
          background: 'transparent',
          border: `1px solid ${C.b}`,
          borderRadius: 8,
          color: C.sub,
          fontSize: 12,
          fontFamily: F.body,
          cursor: 'pointer',
          marginBottom: 12,
        }}
      >
        {explanationOpen ? 'Скрыть пояснение' : 'Зачем это нужно?'}
      </button>
      {explanationOpen && (
        <div
          style={{
            marginBottom: 16,
            padding: '12px 14px',
            background: C.bg,
            border: `1px solid ${C.b}`,
            borderRadius: 10,
            fontSize: 13,
            lineHeight: 1.5,
            color: C.txt,
          }}
        >
          <p style={{ margin: '0 0 8px 0' }}>
            <b style={{ color: C.acc }}>Часовой пояс</b> определяет, когда отправлять
            уведомления (утренняя сводка, напоминания о выезде) — без него эти функции не работают.
          </p>
          <p style={{ margin: '0 0 8px 0' }}>
            <b style={{ color: C.acc }}>Юридические данные</b> (ИНН, ОГРН, директор, банк)
            нужны для оформления актов, счетов и договоров.
          </p>
          <p style={{ margin: '0 0 8px 0' }}>
            <b style={{ color: C.acc }}>Налоговый режим</b> влияет на формат счетов
            (НДС/без НДС).
          </p>
          <p style={{ margin: 0 }}>
            <b style={{ color: C.acc }}>Адрес</b> определяется через подсказки —
            автоматически подставляются координаты и часовой пояс.
          </p>
        </div>
      )}

      {/* ─── Идентификация ─── */}
      <div style={sectionTitleStyle}>Идентификация</div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Юридическая форма</label>
        <select
          value={form.legal_form ?? ''}
          onChange={(e) => update('legal_form', (e.target.value || null) as LegalForm | null)}
          style={inputStyle}
        >
          <option value="">— Не указано —</option>
          {LEGAL_FORM_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>ИНН</label>
          <input
            type="text"
            value={form.inn ?? ''}
            onChange={(e) => update('inn', nullable(e.target.value))}
            placeholder="10 или 12 цифр"
            style={inputStyle}
            inputMode="numeric"
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>КПП</label>
          <input
            type="text"
            value={form.kpp ?? ''}
            onChange={(e) => update('kpp', nullable(e.target.value))}
            placeholder="9 цифр (у ИП нет)"
            style={inputStyle}
            inputMode="numeric"
          />
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>ОГРН / ОГРНИП</label>
        <input
          type="text"
          value={form.ogrn ?? ''}
          onChange={(e) => update('ogrn', nullable(e.target.value))}
          placeholder="13 или 15 цифр"
          style={inputStyle}
          inputMode="numeric"
        />
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Налоговый режим</label>
        <select
          value={form.tax_regime ?? ''}
          onChange={(e) => update('tax_regime', (e.target.value || null) as TaxRegime | null)}
          style={inputStyle}
        >
          <option value="">— Не указано —</option>
          {TAX_REGIME_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* ─── Адрес и часовой пояс ─── */}
      <div style={sectionTitleStyle}>Адрес и часовой пояс</div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>
          Фактический адрес <span style={{ color: C.acc }}>★</span>
        </label>
        <AddressAutocomplete
          value={form.actual_address ?? ''}
          onChange={(v) => update('actual_address', nullable(v))}
          onSelectSuggestion={handleAddressSelect}
          placeholder="г. Москва, ул. Ленина, 8"
        />
        <div style={{ fontSize: 11, color: C.sub, marginTop: 4 }}>
          При выборе из подсказок автоматически определяется часовой пояс.
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>
          Часовой пояс <span style={{ color: C.acc }}>★ обязательно</span>
        </label>
        <select
          value={form.timezone ?? ''}
          onChange={(e) => update('timezone', e.target.value || null)}
          style={inputStyle}
        >
          <option value="">— Не выбран —</option>
          {TIMEZONE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Юридический адрес</label>
        <input
          type="text"
          value={form.legal_address ?? ''}
          onChange={(e) => update('legal_address', nullable(e.target.value))}
          placeholder="Если отличается от фактического"
          style={inputStyle}
        />
      </div>

      {/* ─── Контакты ─── */}
      <div style={sectionTitleStyle}>Контакты</div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Телефон</label>
          <input
            type="tel"
            value={form.contact_phone ?? ''}
            onChange={(e) => update('contact_phone', nullable(e.target.value))}
            placeholder="+7 900 000-00-00"
            style={inputStyle}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Email</label>
          <input
            type="email"
            value={form.contact_email ?? ''}
            onChange={(e) => update('contact_email', nullable(e.target.value))}
            placeholder="info@example.com"
            style={inputStyle}
          />
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Сайт</label>
        <input
          type="url"
          value={form.website ?? ''}
          onChange={(e) => update('website', nullable(e.target.value))}
          placeholder="https://"
          style={inputStyle}
        />
      </div>

      <BrandingUpload
        companyId={companyId}
        kind="logo"
        label="Логотип компании"
        hint="Показывается в шапке счёта/акта. PNG/JPG/SVG, до 2 МБ."
        value={form.logo_url ?? null}
        onChange={(url) => update('logo_url', url)}
      />

      <BrandingUpload
        companyId={companyId}
        kind="stamp"
        label="Печать организации"
        hint="Ставится на счёт/акт. Лучше PNG с прозрачным фоном."
        value={form.stamp_url ?? null}
        onChange={(url) => update('stamp_url', url)}
      />

      <BrandingUpload
        companyId={companyId}
        kind="signature"
        label="Подпись руководителя"
        hint="Факсимиле подписи. Лучше PNG с прозрачным фоном."
        value={form.signature_url ?? null}
        onChange={(url) => update('signature_url', url)}
      />

      {/* ─── Руководство ─── */}
      <div style={sectionTitleStyle}>Руководство</div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>ФИО директора / владельца</label>
        <input
          type="text"
          value={form.director_name ?? ''}
          onChange={(e) => update('director_name', nullable(e.target.value))}
          placeholder="Иванов Иван Иванович"
          style={inputStyle}
        />
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Должность подписанта</label>
          <input
            type="text"
            value={form.signatory_position ?? ''}
            onChange={(e) => update('signatory_position', nullable(e.target.value))}
            placeholder="Директор / ИП"
            style={inputStyle}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Основание</label>
          <input
            type="text"
            value={form.signatory_basis ?? ''}
            onChange={(e) => update('signatory_basis', nullable(e.target.value))}
            placeholder="Устав / Свидетельство"
            style={inputStyle}
          />
        </div>
      </div>

      {/* ─── Банк ─── */}
      <div style={sectionTitleStyle}>Банковские реквизиты</div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Банк</label>
        <input
          type="text"
          value={form.bank_name ?? ''}
          onChange={(e) => update('bank_name', nullable(e.target.value))}
          placeholder="Название банка"
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Расчётный счёт</label>
        <input
          type="text"
          value={form.bank_account ?? ''}
          onChange={(e) => update('bank_account', nullable(e.target.value))}
          placeholder="20 цифр"
          style={inputStyle}
          inputMode="numeric"
        />
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>БИК</label>
          <input
            type="text"
            value={form.bank_bic ?? ''}
            onChange={(e) => update('bank_bic', nullable(e.target.value))}
            placeholder="9 цифр"
            style={inputStyle}
            inputMode="numeric"
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Корр. счёт</label>
          <input
            type="text"
            value={form.bank_correspondent_account ?? ''}
            onChange={(e) => update('bank_correspondent_account', nullable(e.target.value))}
            placeholder="20 цифр"
            style={inputStyle}
            inputMode="numeric"
          />
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Телефон для СБП</label>
        <input
          type="tel"
          value={form.sbp_phone ?? ''}
          onChange={(e) => update('sbp_phone', nullable(e.target.value))}
          placeholder="+7 900 000-00-00"
          style={inputStyle}
        />
        <div style={{ fontSize: 11, color: C.sub, marginTop: 4 }}>
          Показывается клиенту на экране оплаты как способ перевода по номеру телефона.
        </div>
      </div>

      {/* ─── Ошибка ─── */}
      {error && (
        <div
          style={{
            marginTop: 16,
            padding: '10px 14px',
            background: C.reS,
            border: `1px solid ${C.re}`,
            borderRadius: 10,
            color: C.re,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* ─── Кнопки ─── */}
      <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
        {allowSkip && (
          <Button
            ch="Пропустить"
            onClick={onClose}
            col="ghost"
            sz="md"
          />
        )}
        <Button
          ch="Сохранить"
          onClick={handleSave}
          col="acc"
          sz="md"
          loading={saving}
          disabled={!canSave}
        />
      </div>
    </div>
  );
}
